/* ============================================================
   ÉKO '77 — OPENING HOURS, THE SHARED ENGINE

   "Open now · until 10pm" — worked out in the restaurants' own
   timezone, in the VISITOR's browser. It cannot be rendered in Liquid:
   a Shopify page is cached, so a server-rendered hour would be wrong
   for half the day and a cached page would be wrong for all of it.

   WHY A SECOND FILE AND NOT assets/x-locations.js
   That script does this maths too, but it is welded to ONE section:
   it also owns Home: Locations' cross-fading stage and its scroll
   tilt, and it selects on .x-locations / .x-loc / .x-loc__status. The
   contact page needs the same clock with a completely different DOM —
   a status on a chip in the dark board, the SAME status echoed on the
   card below it, and today's line picked out in a printed week. So the
   clock is lifted out here as a primitive that knows no class names at
   all: it is driven entirely by data attributes, which is what lets
   the next section (Restaurants: The rooms wants exactly this) render
   its own markup and still get the hour for free.
   x-locations.js is left ALONE on purpose — it is shipped, QA'd work,
   and rewriting a live section to prove a point is not a refactor.

   THE CONTRACT — four attributes, no classes

     [data-x-hours-scope]   the wording + the clock. Every user-facing
                            string arrives from section settings, so
                            the line is translatable without touching
                            this file:
                              data-tz              Europe/London
                              data-days            Sunday, Monday, …  (from Sunday)
                              data-open-now        Open now · until [time]
                              data-opens-today     Opens today at [time]
                              data-opens-tomorrow  Closed · opens tomorrow at [time]
                              data-opens-day       Closed · opens [day] at [time]
                              data-shut-today      Closed today · opens [day] at [time]
                              data-closed          Closed
                              data-midday / data-midnight

     [data-x-hours]         A SOURCE. Holds this room's week as JSON —
                            {"open":13,"close":[22,22,22,22,22,23,23]}
                            one closing hour per weekday from Sunday,
                            null for a day the room does not open.
                            Optionally carries data-x-room="<key>".

     [data-x-hours-status]  the line to write, inside a source.

     [data-x-hours-echo]    the SAME line, anywhere else on the page,
                            matched by data-x-room. This is how the
                            contact board's chips read the hours of the
                            cards further down without the merchant
                            typing a week twice — one source of truth,
                            exactly as the static reference does it.

     [data-x-hours-day]     a row of the printed week, with
                            data-days="0,1,2" (from Sunday). Gets
                            .is-today when one of them is today.

   Everything it writes is a class toggle or textContent — never
   innerHTML — so a merchant's own wording can never inject markup.

   Idempotent, and safe in the theme editor: one set of listeners no
   matter how many sections ask for the file, and a full repaint on
   section load / unload / reorder. A tab left open through closing
   time is repainted when it comes back, so it never keeps saying
   "Open now" after the kitchen has shut.
   ============================================================ */
(function () {
  'use strict';

  if (window.__xHours) return;
  window.__xHours = true;

  var DAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /* ---------- the clock ---------- */

  function nowIn(tz) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz || 'Europe/London',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(new Date());
    } catch (e) {
      // an unknown timezone must not take the whole band down with it
      parts = new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(new Date());
    }

    var got = {};
    parts.forEach(function (part) {
      got[part.type] = part.value;
    });

    // 24:00 is how en-GB spells midnight at the start of a day
    var hour = parseInt(got.hour, 10) % 24;
    return { day: DAY_INDEX[got.weekday], mins: hour * 60 + parseInt(got.minute, 10) };
  }

  /* British clock: 13 -> 1pm, 22 -> 10pm, 12 -> midday, 0 -> midnight */
  function spellHour(hour, scope) {
    if (hour === 0 || hour === 24) return scope.getAttribute('data-midnight') || 'midnight';
    if (hour === 12) return scope.getAttribute('data-midday') || 'midday';
    return (hour % 12) + (hour < 12 ? 'am' : 'pm');
  }

  function fill(template, time, day) {
    return String(template || '')
      .replace('[time]', time == null ? '' : time)
      .replace('[day]', day == null ? '' : day);
  }

  function write(el, reading) {
    if (!el) return;
    el.textContent = reading.text;
    el.classList.toggle('is-open', reading.open);
  }

  /* ---------- one room ---------- */

  function read(source, scope) {
    var hours;
    try {
      hours = JSON.parse(source.getAttribute('data-x-hours'));
    } catch (e) {
      return null; // no usable week — leave the line empty rather than guess
    }
    if (!hours || typeof hours.open !== 'number') return null;

    var days = (scope.getAttribute('data-days') || '').split(',').map(function (name) {
      return name.trim();
    });

    var now = nowIn(scope.getAttribute('data-tz'));
    var closeToday = Array.isArray(hours.close) ? hours.close[now.day] : hours.close;
    var openMin = hours.open * 60;
    var opensAt = spellHour(hours.open, scope);

    if (closeToday != null && now.mins >= openMin && now.mins < closeToday * 60) {
      return { open: true, text: fill(scope.getAttribute('data-open-now'), spellHour(closeToday, scope)) };
    }

    if (closeToday != null && now.mins < openMin) {
      return { open: false, text: fill(scope.getAttribute('data-opens-today'), opensAt) };
    }

    // walk forward to the next day the room actually opens
    var next = null;
    for (var step = 1; step <= 7; step++) {
      var day = (now.day + step) % 7;
      var close = Array.isArray(hours.close) ? hours.close[day] : hours.close;
      if (close != null) {
        next = { day: day, step: step };
        break;
      }
    }

    if (!next) return { open: false, text: scope.getAttribute('data-closed') || '' };

    /* The reference keeps two different LEAD-INS and they are not
       interchangeable: a room that is SHUT ALL DAY reads "Closed today ·
       opens …", while a room that has simply finished for the evening reads
       "Closed · opens …".

       The DAY WORD, however, is chosen the same way in both cases. The
       reference's own script is explicit about it:
           (next.step === 1 ? 'tomorrow' : names[next.day])
       i.e. the next open day is written as "tomorrow" whenever it IS
       tomorrow, and only named outright when it is further off. This branch
       used to always name the weekday, so New Cross (shut Mondays, open
       Tuesdays) read "Closed today · opens Tuesday at 1pm" against the
       reference's "Closed today · opens tomorrow at 1pm". */
    if (closeToday == null) {
      var shutToday = scope.getAttribute('data-shut-today');
      if (shutToday) {
        var shutDayWord = next.step === 1
          ? (scope.getAttribute('data-tomorrow') || 'tomorrow')
          : (days[next.day] || '');
        return { open: false, text: fill(shutToday, opensAt, shutDayWord) };
      }
    }

    if (next.step === 1) {
      return { open: false, text: fill(scope.getAttribute('data-opens-tomorrow'), opensAt) };
    }

    return { open: false, text: fill(scope.getAttribute('data-opens-day'), opensAt, days[next.day] || '') };
  }

  function today(scope) {
    return nowIn(scope.getAttribute('data-tz')).day;
  }

  function paint(source) {
    var scope = source.closest('[data-x-hours-scope]');
    if (!scope) return;

    var reading = read(source, scope);
    if (!reading) return;

    write(source.querySelector('[data-x-hours-status]'), reading);

    /* the same reading, echoed wherever else this room appears — the
       chips in the contact board read the cards' hours this way */
    var key = source.getAttribute('data-x-room');
    if (key) {
      var safe = window.CSS && CSS.escape ? CSS.escape(key) : key.replace(/["\\]/g, '');
      document
        .querySelectorAll('[data-x-hours-echo][data-x-room="' + safe + '"]')
        .forEach(function (echo) {
          write(echo, reading);
        });
    }

    // and today's line in the printed week is marked
    var day = String(today(scope));
    source.querySelectorAll('[data-x-hours-day]').forEach(function (row) {
      var days = (row.getAttribute('data-days') || '').split(',').map(function (d) {
        return d.trim();
      });
      row.classList.toggle('is-today', days.indexOf(day) > -1);
    });
  }

  function stamp() {
    document.querySelectorAll('[data-x-hours-scope] [data-x-hours]').forEach(paint);
  }

  /* ---------- wiring ---------- */

  // a tab left open through closing time must not keep saying "Open now"
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) stamp();
  });

  // theme editor: a re-rendered section brings new rooms and drops old ones
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(
    function (evt) {
      document.addEventListener(evt, stamp);
    }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stamp);
  } else {
    stamp();
  }
})();
